import { api } from "core/utility";
import { MemberIntroProfile } from "./types";

const MEMBER_UID = /^\d+_\d+$/;

const cache = new Map<string, MemberIntroProfile>();
const cachedAt = new Map<string, number>();
const inflight = new Map<string, Promise<MemberIntroProfile | null>>();
const generation = new Map<string, number>();
// Signed profile URLs expire after 15 minutes. Refresh before that.
const CARD_CACHE_MS = 10 * 60 * 1000;

const asNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const decodeHtml = (value: string) =>
  value
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&apos;/gi, "'");

const toPlainText = (value: string) =>
  value
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const clean = (value: string | null | undefined) => {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }
  const decoded = toPlainText(decodeHtml(value));
  return decoded || null;
};

const textList = (value: unknown): string[] => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.reduce(
      (items: string[], item) => items.concat(textList(item)),
      []
    );
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    return textList(
      record.name || record.label || record.language || record.title
    );
  }
  if (typeof value !== "string") {
    return [];
  }
  return value
    .split(",")
    .map(part => clean(part))
    .filter((part): part is string => !!part);
};

const textField = (value: unknown): string | null => {
  if (Array.isArray(value)) {
    return textList(value).join(", ") || null;
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return textField(record.name || record.label || record.value);
  }
  if (typeof value !== "string") {
    return null;
  }
  return clean(value);
};

const pictureUrl = (value: unknown): string | null => {
  if (!value) {
    return null;
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    return pictureUrl(
      record.url || record.src || record.profile_picture || record.image
    );
  }
  if (typeof value !== "string") {
    return null;
  }
  let url = decodeHtml(value).trim();
  if (!url || url === "null" || url === "None") {
    return null;
  }
  if (url.startsWith("//")) {
    url = `https:${url}`;
  }
  return url;
};

const normalize = (data: MemberIntroProfile): MemberIntroProfile => {
  const source = data as MemberIntroProfile & Record<string, unknown>;
  return {
    ...data,
    handle: clean(data.handle),
    country: clean(data.country),
    about_me: clean(data.about_me),
    gender: data.gender || null,
    ethnicity: textField(source.ethnicity ?? source.race),
    sexual_orientation: textField(
      source.sexual_orientation ?? source.orientation
    ),
    languages: textList(source.languages ?? source.langs),
    profile_picture: pictureUrl(data.profile_picture),
    age: asNumber(data.age),
    has_tokens: !!data.has_tokens,
    is_fanclub: !!data.is_fanclub,
    admirer_rank: asNumber(data.admirer_rank),
    last_gifts: data.last_gifts || [],
    note: data.note
      ? {
          ...data.note,
          note: clean(data.note.note) || "",
        }
      : null,
  };
};

const fetchCard = (uid: string) => {
  const pending = inflight.get(uid);
  if (pending) {
    return pending;
  }
  const ticket = (generation.get(uid) || 0) + 1;
  generation.set(uid, ticket);
  const request = api.broadcastMemberCard
    .get({}, `?uid=${encodeURIComponent(uid)}&gifts_limit=5`)
    .then(({ data }) => {
      if (generation.get(uid) !== ticket || !data?.uid) {
        return null;
      }
      const card = normalize(data as MemberIntroProfile);
      cache.set(uid, card);
      cachedAt.set(uid, Date.now());
      return card;
    })
    .catch(() => null)
    .finally(() => {
      if (inflight.get(uid) === request) {
        inflight.delete(uid);
      }
    });
  inflight.set(uid, request);
  return request;
};

export const clearMemberCard = (uid: string | null | undefined) => {
  const id = (uid || "").trim();
  if (!id) {
    return;
  }
  cache.delete(id);
  cachedAt.delete(id);
  inflight.delete(id);
  generation.set(id, (generation.get(id) || 0) + 1);
};

export const loadMemberIntro = (
  lookupIds: Array<string | null | undefined>
): Promise<MemberIntroProfile | null> => {
  const uid = lookupIds
    .map(id => (id == null ? "" : String(id).trim()))
    .find(id => MEMBER_UID.test(id));
  if (!uid) {
    return Promise.resolve(null);
  }
  const cached = cache.get(uid);
  const fresh =
    cached && Date.now() - (cachedAt.get(uid) || 0) < CARD_CACHE_MS;
  if (cached && fresh) {
    return Promise.resolve(cached);
  }
  return fetchCard(uid);
};
